/**
 * CrawlSuccessDialog — celebration dialog shown after the user confirms
 * (or skips) website crawl data.  Fires confetti and presents two choices:
 *   • Keep Exploring Demo  (stay on dashboard)
 *   • Continue Customizing Your Practice  (go to setup wizard)
 */
import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material';
import ExploreIcon from '@mui/icons-material/Explore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import confetti from 'canvas-confetti';
import { logger } from '../../../utils/logger';

interface CrawlSuccessDialogProps {
  open: boolean;
  clinicName: string;
  /** True when the user confirmed crawl data; false when they skipped. */
  didConfirm: boolean;
  onExploreDemo: () => void;
  onCustomizePractice: () => void;
  /** Optional wizard completion percentage to show on the customize button */
  completionPercent?: number;
}

function fireConfetti() {
  try {
    // Two bursts for a bigger celebration feel
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'],
      disableForReducedMotion: true,
    });
    setTimeout(() => {
      confetti({
        particleCount: 60,
        spread: 50,
        origin: { y: 0.65, x: 0.55 },
        colors: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'],
        disableForReducedMotion: true,
      });
    }, 200);
  } catch (err) {
    logger.warn('Confetti animation failed:', err);
  }
}

export default function CrawlSuccessDialog({
  open,
  clinicName,
  didConfirm,
  onExploreDemo,
  onCustomizePractice,
  completionPercent,
}: CrawlSuccessDialogProps) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (open && !firedRef.current) {
      firedRef.current = true;
      // Small delay so the dialog animates in first
      setTimeout(fireConfetti, 300);
    }
    if (!open) firedRef.current = false;
  }, [open]);

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
        {/* Celebration icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
            animation: 'gentle-pulse 2s ease-in-out infinite',
            '@keyframes gentle-pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.08)' },
            },
          }}
        >
          <Typography sx={{ fontSize: 36, lineHeight: 1 }}>🎉</Typography>
        </Box>

        {/* Heading */}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {clinicName ? `Welcome, ${clinicName}!` : 'Looking great!'}
        </Typography>

        {/* Body text */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 320, mx: 'auto' }}>
          {didConfirm
            ? "We've saved your practice info from your website. You can always refine it later — what would you like to do next?"
            : "No worries — you can set everything up at your own pace. What would you like to do next?"}
        </Typography>

        {/* Clinical Director role + demo data explanation */}
        <Typography
          variant="body2"
          sx={{
            mb: 3,
            maxWidth: 320,
            mx: 'auto',
            bgcolor: 'action.hover',
            borderRadius: 1,
            px: 2,
            py: 1.5,
            fontSize: '0.8125rem',
            color: 'text.secondary',
          }}
        >
          You&apos;re now <strong>Clinical Director</strong> with full access.
          All demo characters and sample data are still here — use the{' '}
          <strong>character switcher</strong> in the menu to view their
          calendars, notes, and clients.
        </Typography>

        {/* Two choices */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={onCustomizePractice}
            startIcon={<AutoAwesomeIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, py: 1.5 }}
          >
            {completionPercent != null && completionPercent > 0
              ? `Continue Customizing — You're ${completionPercent}% Done!`
              : 'Continue Customizing Your Practice'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={onExploreDemo}
            startIcon={<ExploreIcon />}
            sx={{ textTransform: 'none', fontWeight: 500, py: 1.5 }}
          >
            Keep Exploring the Demo
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
